<script setup lang = "ts">
import {ref} from "vue";
import CarFilterChips from "~/components/filters/CarFilterChips.vue";
import CarFilterOptions from "~/components/filters/CarFilterOptions.vue";
import type {OrderBy} from "~/interfaces/OrderBy";
import {useCarDataStore} from "~/store/carData";
import type {Car} from "~/models/old/Car";

const {topPadding, changeScreenSize} = useHelpers()
const {transformListingCarToCard, getAllCars, setDataToHead} = useCar()
const {parseFilters, parseQueryStructured} = useFilters()
const {carFilters, orders, hasFilterValues, getBaseFilters, getValidFilters} = useCarFilters()
const $q = useQuasar()
const $route = useRoute()

const cars = ref(useState<any>('cars') ?? [])

const isFilterOpen = ref(false)
const isAnimated = ref(false)
const isHorizontal = ref(false)
const isLoading = ref(true)
const page = ref(1)
const selectedOrder = ref<OrderBy>(orders[0])
const carDataStore = useCarDataStore()
const refCVSearch = ref<any>(null)
const scrollPos = ref<number>(0)

const getFilterClass = computed(() => [isFilterOpen.value ? 'open' : '', isAnimated.value ? 'animated' : ''])

const closeFilter = () => {
	isAnimated.value = true
	isFilterOpen.value = false
	setTimeout(() => isAnimated.value = false, 350)
}
const openFilter = () => {
	isAnimated.value = true
	isFilterOpen.value = true
}
const removeFilterValue = (attribute: string) => {
	const base: any = getBaseFilters()
	Object.entries(carFilters.value).forEach((value: any) => {
		const key: string = value[0]
		const filter = (carFilters as any).value[key]
		if (filter.attribute == attribute) {
			(carFilters as any).value[key] = base[key]
			// Si la base es make también quitamos el modelo
			if (key == 'make') {
				(carFilters as any).value.model = base.model
			}
		}
	})
}
const setArrayDataToHead = (data: any[]) => {
	data?.forEach((car: Car) => {
		setDataToHead(car)
	})
}
const filterData = async () => {
	const currentFilters = getValidFilters(carFilters.value)
	const data = {
		orderBy: selectedOrder.value,
		page: page.value,
		filters: currentFilters
	}
	isLoading.value = true
	
	try {
		const response = await getAllCars(data)
		
		if (response.data) {
			cars.value = response.data
			const queryStr = parseFilters({filters: currentFilters, orderBy: selectedOrder.value})
			changePath(queryStr)
			setArrayDataToHead(cars.value)
		}
	} catch (e) {
		console.log("Error", e)
	}
	
	isLoading.value = false
	
}
const parseQueryToFilters = (filters: any) => {
	filters.forEach((filter: any) => {
		Object.entries(carFilters.value).forEach(value => {
			const key = value[0]
			const filter_ = value[1]
			if (filter.attribute == filter_.attribute) {
				(carFilters as any).value[key] = filter
			}
		})
	})
}
const parseQueryMain = (query: any) => {
	if (query.condition && query.condition !== '') {
		(carFilters as any).value.condition.value = query.condition
	}
	if (query.make && query.make !== 'Todas las marcas') {
		(carFilters as any).value.make.value = query.make
	}
	if (query.model && query.model !== 'Todos los modelos') {
		(carFilters as any).value.model.value = query.model
	}
	if (query.max_price && query.max_price !== 'Sin Precio Máximo') {
		(carFilters as any).value.price.value.max = query.max_price
	}
	if (query.mileage && query.mileage !== 'Sin Millaje máximo') {
		(carFilters as any).value.millage.value.max = query.mileage
	}
}
const changePath = (queryStr: string) => {
	const hasMake = !!carFilters.value.make.value && carFilters.value.make.value !== ''
	const query = queryStr !== '' ? `?${queryStr}` : ''
	let path = $route.path
	const make = (carFilters.value.make.value + '').replaceAll(' ', '-').toLowerCase() ?? ''
	
	if (hasMake && (!$route.path.includes('marca') || !$route.path.includes(make))) {
		path = `/carros/marca/${make}`
	} else if (!hasMake && $route.path.includes('marca')) {
		path = `/carros`
	}
	
	if (page.value > 1) {
		if (path.includes('pagina')) {
			path = path.replace(/\/pagina\/\d+/, `/pagina/${page.value}`)
		} else {
			path = `${path}/pagina/${page.value}`
		}
	} else if (path.includes('pagina')) {
		path = path.replace(/\/pagina\/\d+/, '')
	}
	
	window.history.replaceState({}, '', `${path}${query}`)
	
}
const clearFilter = () => {
	carFilters.value.condition.value = ''
	carFilters.value.make.value = ''
	carFilters.value.model.value = ''
	carFilters.value.price.value = {
		min: '',
		max: ''
	}
	carFilters.value.millage.value = {
		min: '',
		max: ''
	}
	carFilters.value.year.value = {
		min: '',
		max: ''
	}
	carFilters.value.body_style.value = []
	carFilters.value.fuel_type.value = []
	carFilters.value.transmission.value = []
	carFilters.value.drive_train.value = []
	carFilters.value.color.value = []
	carFilters.value.interior_color.value = []
	carFilters.value.interior_type.value = []
	page.value = 1
	filterData()
}
const changePage = () => {
	if (!isLoading.value) {
		filterData()
		window.scrollTo(0, 0)
	}
}
const onScroll = (position: number) => {
	scrollPos.value = Math.min(position, 100) / 100
}

onMounted(() => {
	changeScreenSize()
	if ($route.query) {
		const query = parseQueryStructured($route.query)
		if (query.filters) {
			parseQueryToFilters(query.filters)
		} else if (query.condition || query.make || query.model || query.max_price || query.mileage) {
			parseQueryMain(query)
		}
		if (query.orderBy) {
			selectedOrder.value = query.orderBy
		}
	}
	
	if ($route.params?.make || $route.params?.page) {
		setTimeout(() => {
			if ($route.params?.make) {
				const make: string = $route.params.make as string
				const makes = carDataStore.getMakes
				carFilters.value.make.value = makes.find((m: any) => (m + '').replaceAll(' ', '-').toLowerCase() == make) ?? ''
				refCVSearch.value?.getModels(carFilters.value.make.value, false)
			}
			if ($route.params?.page) {
				page.value = Number.parseInt(`${$route.params.page}`)
			}
			setTimeout(() => {
				isLoading.value = false
			}, 500)
		}, 500)
	} else {
		isLoading.value = false
	}
	
	setArrayDataToHead(cars.value.data)
	
	
})
watch(carFilters.value, () => {
	if (!isLoading.value) {
		page.value = 1
		filterData()
	}
})
watch(selectedOrder, () => {
	if (!isLoading.value)
		filterData()
})
</script>

<template>
	<section class = "w-full" v-scroll = "onScroll" :style = "`margin-top: ${topPadding}px`">
		<div class = "flex flex-row flex-nowrap gap-x-5 min-h-[100vh]">
			<!-- Overlay Filter -->
			<div class = "overlay-filter absolute top-0 left-0 w-full h-[100vh] bg-neutral-900/20"
					 :class = "[...getFilterClass]" @click = "closeFilter"/>
			<!-- Filter Zone -->
			<div class = "filter-zone bg-white shadow" :class = "[...getFilterClass]">
				<CarFilterOptions ref = "refCVSearch" v-model = "carFilters"
													:show-clear-filters = "hasFilterValues"
													@close-dialog = "closeFilter"
													@clear-filter = "clearFilter"/>
			</div>
			<!-- Content -->
			<div class = " w-full py-5">
				<div class = "flex flex-col">
					<!-- Filter navigation btn -->
					<div class = "sticky top-0 bg-white z-10 px-2 min-[1024px]:hidden"
							 :style = "{paddingTop:`${(scrollPos * (topPadding+10))}px`}">
						<div class = "show-filter mb-5 justify-start px-1">
							<q-btn color = "negative" @click = "openFilter">
								<i class = "fa-solid fa-sliders mr-2"></i> Filtros
							</q-btn>
						</div>
					</div>
					<!-- Title -->
					<div>
						<div class = "flex flex-row flex-nowrap gap-x-5 px-2 justify-between items-center">
							<div>
								<h2 class = "text-xl">Carros</h2>
							</div>
							<div>
								<q-btn-toggle
										name = "genre"
										unelevated
										v-model = "isHorizontal"
										size = "12px"
										padding = "5px 10px"
										toggle-color = "primary"
										class = "border border-solid border-neutral-300"
										:options = "[{label: '', value: true, slot:'horizontal'},{label: '', value: false, slot:'vertical'}]"
								>
									<template v-slot:horizontal>
										<i class = "fa-solid fa-grip"/>
									</template>
									<template v-slot:vertical>
										<i class = "fa-solid fa-grip-vertical"/>
									</template>
								</q-btn-toggle>
							</div>
						</div>
					</div>
					<!-- Order -->
					<div class = "flex flex-row mt-2 px-2" :class = "[hasFilterValues?'mb-0':'mb-5']">
						<div>
							<p>
								{{ isLoading ? 'Cargando' : cars.total }}
								{{ cars.total > 1 || isLoading ? 'resultados' : 'resultado' }}
							</p>
						</div>
						<q-space/>
						<div>
							<q-btn-dropdown
									class = "text-xs"
									padding = "xs sm"
									align = "right"
									flat
									:label = "`${selectedOrder.label}`"
									color = "primary">
								<q-list>
									<q-item v-for = "o in orders" clickable v-close-popup @click = "selectedOrder=o">
										<q-item-section>
											<q-item-label>{{ o.label }}</q-item-label>
										</q-item-section>
									</q-item>
								</q-list>
							</q-btn-dropdown>
						</div>
					</div>
					<!-- Filter Chips -->
					<div v-if = "hasFilterValues" class = "my-2 px-2">
						<CarFilterChips v-model = "carFilters" @remove-filter-value = "removeFilterValue"
														:is-loading = "isLoading"/>
					</div>
					<!-- List -->
					<div>
						<div class = "flex flex-row gap-x-3 gap-y-5 justify-center min-[1123px]:justify-start">
							<CVCardList v-for = "(car, index) in cars.data"
													:is-vertical = "!isHorizontal"
													v-if = "!isLoading"
													:breakpoint = "620"
													max-width = "full"
													:product = "transformListingCarToCard(car as any)"/>
							<q-skeleton height = "200px" style = "min-width: 320px; max-width: 450px; margin:15px auto"
													class = "my-5 w-full"
													v-else
													v-for = "n in 15"/>
						</div>
					</div>
					<!-- Pagination -->
					<div class = "mt-5 mb-5 flex flex-row justify-center">
						<q-pagination
								v-model = "page"
								v-if = "(cars.last_page ?? 1)>1"
								color = "primary"
								flat
								:max = "cars.last_page??1"
								:max-pages = "5"
								@update:model-value = "changePage"
								boundary-links
								boundary-numbers
								direction-links
						/>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>


<style>
.filter-zone {
	width: 100%;
	max-width: 350px;
	min-width: 320px;
}

.overlay-filter {
	display: none;
}

.show-filter {
	display: none;
}


@media screen and (max-width: 1024px) {
	.show-filter {
		display: flex;
	}
	
	.filter-zone {
		position: fixed;
		padding-top: 120px;
		overflow: auto;
		top: 0;
		left: 0;
		width: 320px;
		height: 100vh;
		z-index: 999;
		transform: translateX(-100%);
	}
	
	.filter-zone.animated, .overlay-filter.animated {
		transition: all 0.3s ease-in-out;
	}
	
	.overlay-filter {
		opacity: 0;
		z-index: 998;
		display: none;
	}
	
	.overlay-filter.open {
		position: fixed;
		opacity: 1;
		display: block;
	}
	
	.filter-zone.open {
		transform: translateX(0);
	}
}

@media screen and (max-width: 860px) {
	.filter-zone {
		padding-top: 70px;
	}
}
</style>